import { AsyncMultiSelectField, Button, CharField, Flex, SingleSelectField } from "@inmagik/magik-ui"
import { Formik, getIn, useFormikContext } from "formik"
import { useCallback, useEffect, useRef, useState, type RefObject } from "react"
import { Card, CardHeader, Col, Modal, ModalBody, ModalFooter, ModalHeader, Table } from "react-bootstrap"
import { v4 as uuidv4 } from "uuid"
import { WysiwygField } from "../../components/WysiwygField"
import { useDocumentsList } from "../../hooks/documents"
import { useQsFilters } from "../../hooks/filters"
import { useMediaList } from "../../hooks/media"
import { useMunicipalitiesLazy, useMunicipalityLazy } from "../../hooks/municipalities"
import type { FiltersBase } from "../../types"
import yup from "../../utils/validations"
type Block = {
  blockType: string
  uuid: string
  [key: string]: any
}

type ModalBlockProps = {
  block: Block | null
  setCurrentBlock: (block: Block | null) => void
  onClose: () => void
  editBlock: (block: Block) => void
}

const initFilters = (params: URLSearchParams) =>
  ({
    page: Number(params.get("page") ?? 1),
    search: params.get("search") ?? "",
    ordering: params.get("ordering") ?? "name",
  }) as FiltersBase

function ImageSelector({}: {}) {
  const { filters, uiFilters, setFilters } = useQsFilters(initFilters)
  const { values, setFieldValue } = useFormikContext<Block>()
  const { data } = useMediaList({
    search: filters.search,
    page_size: "1000",
  })

  useEffect(() => {
    const blockNotes = document.getElementById("note-block-modal")
    if (blockNotes) {
      if (values.images) {
        blockNotes.innerHTML = `<span class="text-muted">Hai selezionato ${values.images.length} immagini.</span>`
      } else {
        blockNotes.innerHTML = ""
      }
    }
  }, [values.images])

  return (
    <div style={{ maxHeight: 500, overflowY: "auto", position: "relative", minHeight: 500 }}>
      <div style={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 2, paddingBottom: 8 }}>
        <CharField
          label="Cerca Immagine"
          placeholder="Cerca..."
          leadingIcon="search"
          style={{
            width: "100%",
          }}
          value={uiFilters.search}
          onChange={(value) => {
            setFilters({ ...filters, search: value, page: 1 })
          }}
        />
      </div>
      <Table hover responsive className="mt-3">
        <thead style={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 1 }}>
          <tr>
            <th>Seleziona</th>
            <th>Immagine</th>
          </tr>
        </thead>
        <tbody>
          {data?.results?.map((media) => (
            <tr key={media.id}>
              <td>
                <input
                  type="checkbox"
                  checked={values.images?.includes(media.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const newSelected = values.images ? [...values.images, media.id] : [media.id]
                      setFieldValue("images", newSelected)
                    } else {
                      const newSelected = values.images.filter((id: number) => id !== media.id)
                      setFieldValue("images", newSelected)
                    }
                  }}
                />
              </td>
              <td>
                {media?.media_file && (
                  <img src={media.media_file as string} alt={media.title} style={{ width: 100, height: "auto" }} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

function DocumentSelector({}: {}) {
  const { filters, uiFilters, setFilters } = useQsFilters(initFilters)
  const { values, setFieldValue } = useFormikContext<Block>()
  const { data } = useDocumentsList({
    search: filters.search,
    page_size: "1000",
  })

  useEffect(() => {
    const blockNotes = document.getElementById("note-block-modal")
    if (blockNotes) {
      if (values.blockType === "Carosel") {
        blockNotes.innerHTML = `<span class="text-muted">Hai selezionato ${
          values.records?.length || 0
        } documenti.</span>`
      } else {
        blockNotes.innerHTML = ""
      }
    }
  }, [values.records, values.blockType])

  return (
    <div style={{ maxHeight: 500, overflowY: "auto", position: "relative", minHeight: 500 }}>
      <div style={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 2, paddingBottom: 8 }}>
        <CharField
          label="Cerca Documento"
          placeholder="Cerca..."
          leadingIcon="search"
          style={{
            width: "100%",
          }}
          value={uiFilters.search}
          onChange={(value) => {
            setFilters({ ...filters, search: value, page: 1 })
          }}
        />
      </div>
      <Table hover responsive className="mt-3">
        <thead style={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 1 }}>
          <tr>
            <th>Seleziona</th>
            <th style={{ width: "70%" }}>Titolo</th>
            <th>Immagine</th>
          </tr>
        </thead>
        <tbody>
          {data?.results?.map((document) => (
            <tr key={document.id}>
              <td>
                <input
                  type="checkbox"
                  checked={values.records?.includes(document.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const newSelected = values.records ? [...values.records, document.id] : [document.id]
                      setFieldValue("records", newSelected)
                    } else {
                      const newSelected = values.records.filter((id: number) => id !== document.id)
                      setFieldValue("records", newSelected)
                    }
                  }}
                />
              </td>
              <td>{document.title}</td>
              <td>
                {document.media_data && document?.media_data.length > 0 && (
                  <img
                    src={document?.media_data[0].media_file as string}
                    alt={document?.media_data[0].title}
                    style={{ width: 100, height: "auto" }}
                  />
                )}
              </td>
              <td></td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

const optionsBlockTypes = [
  { value: "", label: "Seleziona tipo di blocco" },
  { value: "TextAndTitle", label: "Testo e Titolo" },
  { value: "Quote", label: "Citazione e Autore" },
  { value: "Text", label: "Solo Testo" },
  { value: "Carosel", label: "Carosello Documenti" },
  { value: "Images", label: "Immagini" },
  { value: "Map", label: "Mappa comuni" },
]

function BlockTypeSelector({ onSelect }: { onSelect: (type: string) => void }) {
  const [currentType, setCurrentType] = useState<string>("")
  return (
    <div>
      <SingleSelectField
        value={optionsBlockTypes.find((option) => option.value === currentType) ?? undefined}
        label="Tipo di Blocco"
        layout="horizontal"
        options={optionsBlockTypes}
        onChange={(option) => {
          if (option) {
            setCurrentType(option.value)
            onSelect(option.value)
          }
        }}
      />
    </div>
  )
}

function AutoValidate({ setBlockFormValid }: { setBlockFormValid: (valid: boolean) => void }) {
  const { values, errors, isValid } = useFormikContext<Block>()

  useEffect(() => {
    setBlockFormValid(isValid)
  }, [isValid, values, errors])

  return null
}

function ImagesBlock({
  block,
  saveBlock,
  setBlockFormValid,
  submitRef,
}: {
  block: Block
  saveBlock: (block: Block) => void
  setBlockFormValid: (valid: boolean) => void
  submitRef: RefObject<HTMLButtonElement | null>
}) {
  // Implementazione del blocco immagini qui
  const schema = yup.object().shape({
    images: yup
      .array()
      // minimo una immagine
      .of(yup.number())
      .min(1, "Seleziona almeno un'immagine")
      .required("Devi selezionare almeno un'immagine"),
  })

  const buttonRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (submitRef && submitRef.current) {
      submitRef.current.onclick = () => {
        if (buttonRef.current) {
          buttonRef.current.click()
        }
      }
    }
  }, [submitRef])

  return (
    <Formik
      enableReinitialize
      initialValues={block}
      validateOnMount
      validationSchema={schema}
      onSubmit={(values) => {
        saveBlock(values)
      }}
    >
      {({ handleSubmit }) => {
        return (
          <form onSubmit={handleSubmit}>
            <ImageSelector />
            <button ref={buttonRef} type="submit" style={{ display: "none" }}>
              Salva Blocco
            </button>
            <AutoValidate setBlockFormValid={setBlockFormValid} />
          </form>
        )
      }}
    </Formik>
  )
}

function MapBlock({
  block,
  saveBlock,
  setBlockFormValid,
  submitRef,
}: {
  block: Block
  saveBlock: (block: Block) => void
  setBlockFormValid: (valid: boolean) => void
  submitRef: RefObject<HTMLButtonElement | null>
}) {
  const fetchMunicipality = useMunicipalityLazy()
  const fetchMunicipalities = useMunicipalitiesLazy()
  const schema = yup.object().shape({
    municipalities: yup
      .array()
      .of(yup.number())
      .min(1, "Seleziona almeno un comune per la mappa")
      .required("Devi selezionare almeno un comune per la mappa"),
  })

  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (submitRef && submitRef.current) {
      submitRef.current.onclick = () => {
        if (buttonRef.current) {
          buttonRef.current.click()
        }
      }
    }
  }, [submitRef])

  return (
    <Formik
      enableReinitialize
      initialValues={block}
      validateOnMount
      validationSchema={schema}
      onSubmit={(values) => {
        saveBlock(values)
      }}
    >
      {({ handleSubmit, values, errors, setFieldValue }) => {
        return (
          <form onSubmit={handleSubmit}>
            {/* Implementazione del blocco mappa qui */}
            <AsyncMultiSelectField
              label="Comuni"
              layout="horizontal"
              required
              placeholder="Seleziona uno o più comuni"
              onChange={(value) => {
                setFieldValue("municipalities", value)
              }}
              clearable
              value={values.municipalities || []}
              loadInitialValues={(ids: number[]) =>
                Promise.all(
                  ids?.map((id) =>
                    fetchMunicipality(id).then((municipality) => ({
                      label: municipality.name,
                      value: municipality.id,
                    })),
                  ),
                )
              }
              loadOptions={(inputValue: string) => {
                return fetchMunicipalities({ search: inputValue }).then((municipalities) => {
                  return municipalities.results?.map((municipality) => ({
                    label: municipality.name,
                    value: municipality.id,
                  }))
                })
              }}
              isValid={getIn(errors, "municipalities") ? false : undefined}
              errorMessage={getIn(errors, "municipalities")}
            />

            <button ref={buttonRef} type="submit" style={{ display: "none" }}>
              Salva Blocco
            </button>
            <AutoValidate setBlockFormValid={setBlockFormValid} />
          </form>
        )
      }}
    </Formik>
  )
}

function ImageCarouselBlock({
  block,
  saveBlock,
  setBlockFormValid,
  submitRef,
}: {
  block: Block
  saveBlock: (block: Block) => void
  setBlockFormValid: (valid: boolean) => void
  submitRef: RefObject<HTMLButtonElement | null>
}) {
  const schema = yup.object().shape({
    records: yup
      .array()
      .of(yup.number())
      .min(1, "Seleziona almeno un documento per il carosello")
      .required("Devi selezionare almeno un documento per il carosello"),
  })

  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (submitRef && submitRef.current) {
      submitRef.current.onclick = () => {
        if (buttonRef.current) {
          buttonRef.current.click()
        }
      }
    }
  }, [submitRef])

  return (
    <Formik
      enableReinitialize
      initialValues={block}
      validateOnMount
      validationSchema={schema}
      onSubmit={(values) => {
        saveBlock(values)
      }}
    >
      {({ handleSubmit }) => {
        return (
          <form onSubmit={handleSubmit}>
            <DocumentSelector />
            <button ref={buttonRef} type="submit" style={{ display: "none" }}>
              Salva Blocco
            </button>
            <AutoValidate setBlockFormValid={setBlockFormValid} />
          </form>
        )
      }}
    </Formik>
  )
}

function TextBlock({
  block,
  saveBlock,
  setBlockFormValid,
  submitRef,
}: {
  block: Block
  saveBlock: (block: Block) => void
  setBlockFormValid: (valid: boolean) => void
  submitRef: RefObject<HTMLButtonElement | null>
}) {
  const schema = yup.object().shape({
    content: yup.string().required("Il contenuto è obbligatorio"),
  })

  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (submitRef && submitRef.current) {
      submitRef.current.onclick = () => {
        if (buttonRef.current) {
          buttonRef.current.click()
        }
      }
    }
  }, [submitRef])
  // Implementazione del blocco di testo qui
  return (
    <Formik
      enableReinitialize
      initialValues={block}
      validateOnMount
      validationSchema={schema}
      onSubmit={(values) => {
        saveBlock(values)
      }}
    >
      {({ handleSubmit, values, errors, submitCount, setFieldValue }) => {
        return (
          <form onSubmit={handleSubmit}>
            <WysiwygField
              label="Testo"
              layout="horizontal"
              value={values.content || ""}
              onChange={(value) => {
                setFieldValue("content", value)
              }}
              isValid={submitCount > 0 ? !getIn(errors, "content") : undefined}
              errorMessage={getIn(errors, "content")}
            />
            <button ref={buttonRef} type="submit" style={{ display: "none" }}>
              Salva Blocco
            </button>
            <AutoValidate setBlockFormValid={setBlockFormValid} />
          </form>
        )
      }}
    </Formik>
  )
}

function TextAndTitleBlock({
  block,
  saveBlock,
  setBlockFormValid,
  submitRef,
}: {
  block: Block
  saveBlock: (block: Block) => void
  setBlockFormValid: (valid: boolean) => void
  submitRef: RefObject<HTMLButtonElement | null>
}) {
  const schema = yup.object().shape({
    title: yup.string().required("Il titolo è obbligatorio"),
    content: yup.string().required("Il contenuto è obbligatorio"),
  })

  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (submitRef && submitRef.current) {
      submitRef.current.onclick = () => {
        if (buttonRef.current) {
          buttonRef.current.click()
        }
      }
    }
  }, [submitRef])

  return (
    <Formik
      enableReinitialize
      initialValues={block}
      validateOnMount
      validationSchema={schema}
      onSubmit={(values) => {
        saveBlock(values)
      }}
    >
      {({ handleSubmit, values, errors, setFieldValue }) => {
        return (
          <form onSubmit={handleSubmit}>
            <CharField
              label="Titolo"
              layout="horizontal"
              value={values.title}
              onChange={(value) => {
                setFieldValue("title", value)
              }}
              isValid={!getIn(errors, "title")}
              errorMessage={getIn(errors, "title")}
              required
              className="mb-3"
            />
            <WysiwygField
              label="Testo"
              layout="horizontal"
              value={values.content}
              onChange={(value) => {
                setFieldValue("content", value)
              }}
              required
              isValid={!getIn(errors, "content")}
              errorMessage={getIn(errors, "content")}
            />
            <button ref={buttonRef} type="submit" style={{ display: "none" }}>
              Salva Blocco
            </button>
            <AutoValidate setBlockFormValid={setBlockFormValid} />
          </form>
        )
      }}
    </Formik>
  )
}

function QuoteBlock({
  block,
  saveBlock,
  setBlockFormValid,
  submitRef,
}: {
  block: Block
  saveBlock: (block: Block) => void
  setBlockFormValid: (valid: boolean) => void
  submitRef: RefObject<HTMLButtonElement | null>
}) {
  const schema = yup.object().shape({
    author: yup.string().required("L'autore è obbligatorio"),
    content: yup.string().required("Il contenuto è obbligatorio"),
  })

  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (submitRef && submitRef.current) {
      submitRef.current.onclick = () => {
        if (buttonRef.current) {
          buttonRef.current.click()
        }
      }
    }
  }, [submitRef])

  return (
    <Formik
      enableReinitialize
      initialValues={block}
      validateOnMount
      validationSchema={schema}
      onSubmit={(values) => {
        saveBlock(values)
      }}
    >
      {({ handleSubmit, values, errors, setFieldValue }) => {
        return (
          <form onSubmit={handleSubmit}>
            <CharField
              label="Autore"
              layout="horizontal"
              value={values.author}
              onChange={(value) => {
                setFieldValue("author", value)
              }}
              isValid={!getIn(errors, "author")}
              errorMessage={getIn(errors, "author")}
              required
              className="mb-3"
            />
            <WysiwygField
              label="Testo"
              layout="horizontal"
              value={values.content}
              onChange={(value) => {
                setFieldValue("content", value)
              }}
              required
              isValid={!getIn(errors, "content")}
              errorMessage={getIn(errors, "content")}
            />
            <button ref={buttonRef} type="submit" style={{ display: "none" }}>
              Salva Blocco
            </button>
            <AutoValidate setBlockFormValid={setBlockFormValid} />
          </form>
        )
      }}
    </Formik>
  )
}

function ModalBlock({ block, setCurrentBlock, onClose, editBlock }: ModalBlockProps) {
  const submitRef = useRef<HTMLButtonElement>(null)
  const [blockFormValid, setBlockFormValid] = useState<boolean>(false)

  const saveBlock = useCallback(
    (block: Block) => {
      if (block) {
        setCurrentBlock(block)
        editBlock(block)
        onClose()
      }
    },
    [block, editBlock, onClose],
  )

  return (
    <Modal show={!!block} onHide={onClose} centered size="lg">
      <ModalHeader closeButton>
        <Modal.Title>{block?.uuid ? "Modifica Blocco" : "Aggiungi Blocco"}</Modal.Title>
      </ModalHeader>
      <ModalBody>
        {block?.blockType ? (
          // switch block type to render appropriate form
          (block.blockType === "Quote" && (
            <QuoteBlock
              block={block}
              saveBlock={saveBlock}
              setBlockFormValid={setBlockFormValid}
              submitRef={submitRef}
            />
          )) ||
          (block.blockType === "TextAndTitle" && (
            <TextAndTitleBlock
              block={block}
              saveBlock={saveBlock}
              setBlockFormValid={setBlockFormValid}
              submitRef={submitRef}
            />
          )) ||
          (block.blockType === "Text" && (
            <TextBlock
              block={block}
              saveBlock={saveBlock}
              setBlockFormValid={setBlockFormValid}
              submitRef={submitRef}
            />
          )) ||
          (block.blockType === "Carosel" && (
            <ImageCarouselBlock
              block={block}
              saveBlock={saveBlock}
              setBlockFormValid={setBlockFormValid}
              submitRef={submitRef}
            />
          )) ||
          (block.blockType === "Map" && (
            <MapBlock block={block} saveBlock={saveBlock} setBlockFormValid={setBlockFormValid} submitRef={submitRef} />
          )) ||
          (block.blockType === "Images" && (
            <ImagesBlock
              block={block}
              saveBlock={saveBlock}
              setBlockFormValid={setBlockFormValid}
              submitRef={submitRef}
            />
          ))
        ) : (
          <BlockTypeSelector
            onSelect={(type) => {
              if (block) {
                setCurrentBlock({
                  ...block,
                  blockType: type,
                })
              }
            }}
          />
        )}
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between">
        <div id="note-block-modal"></div>
        <div className="d-flex gap-2">
          <Button type="button" variant="secondary" onClick={onClose} title="Chiudi" className="text-white" size="md" />
          <button ref={submitRef} disabled={!blockFormValid} type="button" className="btn btn-primary btn-md">
            Salva
          </button>
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default function BlogEditorForm({
  col = 8,
  fieldName,
  currentValue,
  onSave,
}: {
  col?: number
  fieldName?: string
  currentValue: any
  onSave: (values: any) => void
}) {
  const [currentBlock, setCurrentBlock] = useState<Block | null>(null)

  const editBlock = useCallback(
    (block: Block) => {
      const blockIndex = currentValue.findIndex((b: Block) => b.uuid === block.uuid)
      if (blockIndex !== -1) {
        const newValue = [...currentValue]
        newValue[blockIndex] = block
        onSave(newValue)
      } else {
        const newValue = [...currentValue, block]
        onSave(newValue)
      }
    },
    [fieldName, onSave, currentValue],
  )

  const addBlock = useCallback(() => {
    const uuid = uuidv4()
    setCurrentBlock({
      uuid,
    } as Block)
  }, [])

  return (
    <Col xs={col} className="d-flex flex-column align-items-stretch gap-4">
      {(currentValue || []).map((block: Block, index: number) => {
        return (
          <Card key={block.uuid}>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <strong>
                {optionsBlockTypes.find((opt) => opt.value === block.blockType)?.label || "Tipo non selezionato"}
              </strong>
              <Flex gap={4}>
                <Button
                  size="sm"
                  title=""
                  disabled={
                    // if is the first block
                    index === 0 || !(currentValue && currentValue.length > 1)
                  }
                  onClick={() => {
                    const blockIndex = currentValue.findIndex((b: Block) => b.uuid === block.uuid)
                    if (blockIndex > 0) {
                      const newValue = [...currentValue]
                      const temp = newValue[blockIndex - 1]
                      newValue[blockIndex - 1] = newValue[blockIndex]
                      newValue[blockIndex] = temp
                      onSave(newValue)
                    }
                  }}
                  className="text-white"
                  variant="secondary"
                  leadingIcon="arrow_upward"
                />
                <Button
                  size="sm"
                  title=""
                  disabled={index === currentValue.length - 1 || !(currentValue && currentValue.length > 1)}
                  onClick={() => {
                    const blockIndex = currentValue.findIndex((b: Block) => b.uuid === block.uuid)
                    if (blockIndex < currentValue.length - 1) {
                      const newValue = [...currentValue]
                      const temp = newValue[blockIndex + 1]
                      newValue[blockIndex + 1] = newValue[blockIndex]
                      newValue[blockIndex] = temp
                      onSave(newValue)
                    }
                  }}
                  className="text-white"
                  variant="secondary"
                  leadingIcon="arrow_downward"
                />
                <Button
                  size="sm"
                  title=""
                  onClick={() => {
                    setCurrentBlock(block)
                  }}
                  leadingIcon="edit"
                />
                <Button
                  size="sm"
                  variant="danger"
                  title=""
                  onClick={() => {
                    const newValue = currentValue.filter((b: Block) => b.uuid !== block.uuid)
                    onSave(newValue)
                  }}
                  leadingIcon="delete"
                />
              </Flex>
            </CardHeader>
          </Card>
        )
      })}
      <Button size="md" title="Aggiungi Blocco" onClick={addBlock} leadingIcon="add" style={{ width: "fit-content" }} />
      {currentBlock && (
        <ModalBlock
          block={currentBlock}
          setCurrentBlock={setCurrentBlock}
          onClose={() => {
            setCurrentBlock(null)
          }}
          editBlock={editBlock}
        />
      )}
    </Col>
  )
}
